import BugReportIcon from "@mui/icons-material/BugReport";
import ExploreIcon from "@mui/icons-material/Explore";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { getLabServerUrl, isDev } from "../../utils";
import { DecisionLog } from "./components/DecisionLog";
import { HangarCanvas } from "./components/HangarCanvas";
import type {
  Aircraft,
  ExploreResponse,
  RunScenarioResponse,
  ScenarioSummary,
  ScoringMode,
} from "./types";
import { computeExtractionOrder } from "./utils/extractionOrder";

// Debug placement response type
interface DebugPlacement {
  aircraft_id: string;
  name: string;
  x: number;
  y: number;
  angle: number;
  geometry: GeoJSON.Polygon;
  components?: {
    wing: {
      geom: GeoJSON.Polygon | GeoJSON.MultiPolygon | null;
      top: number;
      bottom: number;
    };
    tail: {
      geom: GeoJSON.Polygon | GeoJSON.MultiPolygon | null;
      top: number;
      bottom: number;
    };
    body: {
      geom: GeoJSON.Polygon | GeoJSON.MultiPolygon | null;
      top: number;
      bottom: number;
      fuselageBottom?: number;
    };
    verticalTailFin: {
      geom: GeoJSON.Polygon | GeoJSON.MultiPolygon | null;
      top: number;
      bottom: number;
    };
  };
  debug: {
    centroid: [number, number];
    inverted: boolean;
    bboxMain: { minX: number; minY: number; maxX: number; maxY: number };
    bboxComponents: Record<
      string,
      { minX: number; minY: number; maxX: number; maxY: number }
    >;
  };
}

interface DebugResponse {
  placements: DebugPlacement[];
  collision: {
    detected: boolean;
    pairs: Array<{ a: string; b: string; reason: string }>;
    verticalSpacing: number;
  };
}

// Note: Debug mode passes minimal aircraft data to HangarCanvas
// The component only uses `model` and `id` for debug visualization

const VALID_SCORING_MODES: ScoringMode[] = ["compact", "compact-subsampled"];
const VALID_ENGINES = ["typescript", "cpp"] as const;
type Engine = (typeof VALID_ENGINES)[number];

const isValidScoringMode = (mode: string | null): mode is ScoringMode => {
  return mode !== null && VALID_SCORING_MODES.includes(mode as ScoringMode);
};

const isValidEngine = (engine: string | null): engine is Engine => {
  return engine !== null && VALID_ENGINES.includes(engine as Engine);
};

export const AutoStackingLab: React.FC = () => {
  // Production safety: Only available in development
  if (!isDev()) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h6" color="text.secondary">
          Auto Stacking Lab is only available in development mode.
        </Typography>
      </Box>
    );
  }

  const labServerUrl = getLabServerUrl();
  const [searchParams, setSearchParams] = useSearchParams();
  const [scenarios, setScenarios] = useState<ScenarioSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [exploring, setExploring] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<RunScenarioResponse | null>(null);
  const [exploreResult, setExploreResult] = useState<ExploreResponse | null>(
    null
  );
  const [selectedVariantIndex, setSelectedVariantIndex] = useState(0);
  const [showComponents, setShowComponents] = useState(false);
  const [showAircraftLabels, setShowAircraftLabels] = useState(true);
  const [showHeightLabels, setShowHeightLabels] = useState(true);
  const [showTowability, setShowTowability] = useState(false);
  const [showMagneticLines, setShowMagneticLines] = useState(false);
  const [showDeadZones, setShowDeadZones] = useState(true);
  const [showObstructions, setShowObstructions] = useState(true);
  const [showObstaclePlacements, setShowObstaclePlacements] = useState(true);
  const [showBays, setShowBays] = useState(true);
  const [showGarageDoors, setShowGarageDoors] = useState(true);
  const [showTowAnimation, setShowTowAnimation] = useState(false);
  const [showSweptGeometry, setShowSweptGeometry] = useState(false);
  const [showTugGeometry, setShowTugGeometry] = useState(false);

  // Debug rotation for multi-door development (Phase 1)
  const [debugRotation, setDebugRotation] = useState<0 | 90 | 180 | 270>(0);

  // Debug mode state
  const [debugMode, setDebugMode] = useState(false);
  const [debugLoading, setDebugLoading] = useState(false);
  const [debugResult, setDebugResult] = useState<DebugResponse | null>(null);
  const [debugAircraft1, setDebugAircraft1] = useState("");
  const [debugAircraft2, setDebugAircraft2] = useState("");
  const [debugPos1, setDebugPos1] = useState({ x: "0", y: "0", angle: "0" });
  const [debugPos2, setDebugPos2] = useState({ x: "50", y: "0", angle: "0" });
  const [debugVerticalSpacing, setDebugVerticalSpacing] = useState("0.5");

  // Read from URL params
  const selectedScenarioId = searchParams.get("scenario") || "";
  const scoringModeParam = searchParams.get("mode");
  const scoringMode: ScoringMode = isValidScoringMode(scoringModeParam)
    ? scoringModeParam
    : "compact";
  const engineParam = searchParams.get("engine");
  const engine: Engine = isValidEngine(engineParam) ? engineParam : "typescript";

  // Update URL params helper
  const updateParams = useCallback(
    (updates: { scenario?: string; mode?: string; engine?: string }) => {
      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev);
          Object.entries(updates).forEach(([key, value]) => {
            if (value) {
              newParams.set(key, value);
            } else {
              newParams.delete(key);
            }
          });
          return newParams;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const setSelectedScenarioId = useCallback(
    (id: string) => {
      updateParams({ scenario: id });
    },
    [updateParams]
  );

  const setScoringMode = useCallback(
    (mode: ScoringMode) => {
      updateParams({ mode });
    },
    [updateParams]
  );

  const setEngine = useCallback(
    (engine: Engine) => {
      updateParams({ engine });
    },
    [updateParams]
  );

  // Fetch available scenarios on mount
  useEffect(() => {
    fetchScenarios();
  }, []);

  const fetchScenarios = async () => {
    try {
      const response = await fetch(`${labServerUrl}/scenarios`);
      if (!response.ok) {
        throw new Error(`Failed to fetch scenarios: ${response.statusText}`);
      }
      const data: ScenarioSummary[] = await response.json();
      setScenarios(data);

      // Only set default scenario if none is in URL and scenarios exist
      const currentScenario = searchParams.get("scenario");
      if (data.length > 0 && !currentScenario) {
        setSelectedScenarioId(data[0].id);
      }
    } catch (err) {
      setError(
        `Failed to connect to lab server at ${labServerUrl}. Is it running?`
      );
    }
  };

  const runScenario = async () => {
    if (!selectedScenarioId) return;

    setLoading(true);
    setError(null);
    setResult(null);
    setExploreResult(null);

    try {
      const engineQuery = engine === "cpp" ? "?engine=cpp" : "";
      const response = await fetch(
        `${labServerUrl}/run/${selectedScenarioId}${engineQuery}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ scoringMode }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const data: RunScenarioResponse = await response.json();
      setResult(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to run scenario");
    } finally {
      setLoading(false);
    }
  };

  const exploreScenario = async () => {
    if (!selectedScenarioId) return;

    setExploring(true);
    setError(null);
    setResult(null);
    setExploreResult(null);
    setSelectedVariantIndex(0);

    try {
      const response = await fetch(
        `${labServerUrl}/explore/${selectedScenarioId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            phase1Orderings: 100,
            phase1KeepTop: 10,
            topN: 5,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const data: ExploreResponse = await response.json();
      setExploreResult(data);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to explore scenario"
      );
    } finally {
      setExploring(false);
    }
  };

  // Debug mode: call debug endpoint to check component transformations
  const runDebugCheck = async () => {
    if (!debugAircraft1 || !debugAircraft2) {
      setError("Select both aircraft for debug mode");
      return;
    }

    setDebugLoading(true);
    setError(null);
    setDebugResult(null);

    try {
      const params = new URLSearchParams({
        aircraft1: debugAircraft1,
        x1: debugPos1.x,
        y1: debugPos1.y,
        angle1: debugPos1.angle,
        aircraft2: debugAircraft2,
        x2: debugPos2.x,
        y2: debugPos2.y,
        angle2: debugPos2.angle,
        verticalSpacing: debugVerticalSpacing,
      });

      const response = await fetch(
        `${labServerUrl}/debug/placements?${params}`
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const data: DebugResponse = await response.json();
      setDebugResult(data);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to run debug check"
      );
    } finally {
      setDebugLoading(false);
    }
  };

  // Get unique aircraft from result for debug mode dropdown
  const availableAircraft = useMemo(() => {
    if (!result) return [];
    const seen = new Set<string>();
    return result.placements
      .filter((p) => {
        if (seen.has(p.aircraft_id)) return false;
        seen.add(p.aircraft_id);
        return true;
      })
      .map((p) => ({
        id: p.aircraft_id,
        name: p.aircraft?.model || p.aircraft_id.substring(0, 8),
      }));
  }, [result]);

  const selectedScenario = scenarios.find((s) => s.id === selectedScenarioId);

  // Compute extraction order for the current result (using towPlan from API for collision-based blocking)
  const extractionOrder = useMemo(
    () =>
      result ? computeExtractionOrder(result.placements, result.towPlan) : [],
    [result]
  );

  // Compute extraction order for selected variant in explore mode (using towPlan for collision-based blocking)
  const exploreExtractionOrder = useMemo(() => {
    if (!exploreResult || exploreResult.topVariants.length === 0) return [];
    const variant = exploreResult.topVariants[selectedVariantIndex];
    return variant
      ? computeExtractionOrder(variant.layout.placements, variant.towPlan)
      : [];
  }, [exploreResult, selectedVariantIndex]);

  return (
    <Box sx={{ p: 2, maxWidth: 1400, margin: "0 auto" }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Controls */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <FormControl sx={{ minWidth: 300 }}>
            <InputLabel>Scenario</InputLabel>
            <Select
              value={selectedScenarioId}
              label="Scenario"
              onChange={(e) => setSelectedScenarioId(e.target.value)}
              disabled={loading || scenarios.length === 0}
              sx={{ width: 500 }}
            >
              {scenarios.map((scenario) => (
                <MenuItem key={scenario.id} value={scenario.id}>
                  {scenario.id}: {scenario.description}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Scoring Mode</InputLabel>
            <Select
              value={scoringMode}
              label="Scoring Mode"
              onChange={(e) => setScoringMode(e.target.value as ScoringMode)}
              disabled={loading}
            >
              <MenuItem value="compact">Compact (dense)</MenuItem>
              <MenuItem value="compact-subsampled">
                Compact Subsampled (fast)
              </MenuItem>
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 130 }}>
            <InputLabel>Engine</InputLabel>
            <Select
              value={engine}
              label="Engine"
              onChange={(e) => setEngine(e.target.value as Engine)}
              disabled={loading}
            >
              <MenuItem value="typescript">TypeScript</MenuItem>
              <MenuItem value="cpp">C++ (native)</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Debug Rotation</InputLabel>
            <Select
              value={debugRotation}
              label="Debug Rotation"
              onChange={(e) =>
                setDebugRotation(e.target.value as 0 | 90 | 180 | 270)
              }
            >
              <MenuItem value={0}>0° (South)</MenuItem>
              <MenuItem value={90}>90° (West)</MenuItem>
              <MenuItem value={180}>180° (North)</MenuItem>
              <MenuItem value={270}>270° (East)</MenuItem>
            </Select>
          </FormControl>

          <Button
            variant="contained"
            startIcon={
              loading ? <CircularProgress size={20} /> : <PlayArrowIcon />
            }
            onClick={runScenario}
            disabled={loading || exploring || !selectedScenarioId}
          >
            {loading ? "Running..." : "Run"}
          </Button>

          <Button
            variant="outlined"
            startIcon={
              exploring ? <CircularProgress size={20} /> : <ExploreIcon />
            }
            onClick={exploreScenario}
            disabled={loading || exploring || !selectedScenarioId}
          >
            {exploring ? "Exploring..." : "Explore Variants"}
          </Button>

          {selectedScenario && (
            <Typography variant="body2" color="text.secondary">
              Hangar: {selectedScenario.hangar.width} x{" "}
              {selectedScenario.hangar.depth} ft | Wall Margin:{" "}
              {selectedScenario.wallMargin ?? 2} ft | H-Spacing:{" "}
              {selectedScenario.horizontalSpacing ?? 2} ft | Aircraft:{" "}
              {selectedScenario.aircraftCount}
              {selectedScenario.overlap && " | 3D Overlap: ON"}
            </Typography>
          )}

          <Box sx={{ ml: "auto" }}>
            <FormControlLabel
              control={
                <Switch
                  checked={debugMode}
                  onChange={(e) => setDebugMode(e.target.checked)}
                  color="warning"
                />
              }
              label="Debug Mode"
            />
          </Box>
        </Box>
      </Paper>

      {/* Debug Mode Controls */}
      {debugMode && (
        <Paper
          sx={{
            p: 2,
            mb: 3,
            bgcolor: "warning.50",
            border: "1px solid",
            borderColor: "warning.main",
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}
          >
            <BugReportIcon color="warning" />
            Debug Mode: Test Component Transformations
          </Typography>

          {availableAircraft.length === 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Run a scenario first to load aircraft data, then use debug mode to
              test specific positions.
            </Alert>
          )}

          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexWrap: "wrap",
              alignItems: "flex-start",
            }}
          >
            {/* Aircraft 1 */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography variant="caption" fontWeight={600}>
                Aircraft 1
              </Typography>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Aircraft</InputLabel>
                <Select
                  value={debugAircraft1}
                  label="Aircraft"
                  onChange={(e) => setDebugAircraft1(e.target.value)}
                  disabled={availableAircraft.length === 0}
                >
                  {availableAircraft.map((a) => (
                    <MenuItem key={a.id} value={a.id}>
                      {a.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box sx={{ display: "flex", gap: 1 }}>
                <TextField
                  size="small"
                  label="X"
                  value={debugPos1.x}
                  onChange={(e) =>
                    setDebugPos1({ ...debugPos1, x: e.target.value })
                  }
                  sx={{ width: 60 }}
                />
                <TextField
                  size="small"
                  label="Y"
                  value={debugPos1.y}
                  onChange={(e) =>
                    setDebugPos1({ ...debugPos1, y: e.target.value })
                  }
                  sx={{ width: 60 }}
                />
                <TextField
                  size="small"
                  label="Angle"
                  value={debugPos1.angle}
                  onChange={(e) =>
                    setDebugPos1({ ...debugPos1, angle: e.target.value })
                  }
                  sx={{ width: 70 }}
                />
              </Box>
            </Box>

            {/* Aircraft 2 */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography variant="caption" fontWeight={600}>
                Aircraft 2
              </Typography>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Aircraft</InputLabel>
                <Select
                  value={debugAircraft2}
                  label="Aircraft"
                  onChange={(e) => setDebugAircraft2(e.target.value)}
                  disabled={availableAircraft.length === 0}
                >
                  {availableAircraft.map((a) => (
                    <MenuItem key={a.id} value={a.id}>
                      {a.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box sx={{ display: "flex", gap: 1 }}>
                <TextField
                  size="small"
                  label="X"
                  value={debugPos2.x}
                  onChange={(e) =>
                    setDebugPos2({ ...debugPos2, x: e.target.value })
                  }
                  sx={{ width: 60 }}
                />
                <TextField
                  size="small"
                  label="Y"
                  value={debugPos2.y}
                  onChange={(e) =>
                    setDebugPos2({ ...debugPos2, y: e.target.value })
                  }
                  sx={{ width: 60 }}
                />
                <TextField
                  size="small"
                  label="Angle"
                  value={debugPos2.angle}
                  onChange={(e) =>
                    setDebugPos2({ ...debugPos2, angle: e.target.value })
                  }
                  sx={{ width: 70 }}
                />
              </Box>
            </Box>

            {/* Vertical Spacing */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography variant="caption" fontWeight={600}>
                Settings
              </Typography>
              <TextField
                size="small"
                label="V-Spacing (ft)"
                value={debugVerticalSpacing}
                onChange={(e) => setDebugVerticalSpacing(e.target.value)}
                sx={{ width: 100 }}
              />
            </Box>

            {/* Check Button */}
            <Box
              sx={{ display: "flex", alignItems: "flex-end", height: "100%" }}
            >
              <Button
                variant="contained"
                color="warning"
                startIcon={
                  debugLoading ? (
                    <CircularProgress size={20} />
                  ) : (
                    <BugReportIcon />
                  )
                }
                onClick={runDebugCheck}
                disabled={debugLoading || !debugAircraft1 || !debugAircraft2}
                sx={{ mt: 2.5 }}
              >
                {debugLoading ? "Checking..." : "Check Collision"}
              </Button>
            </Box>
          </Box>

          {/* Debug Results */}
          {debugResult && (
            <Box
              sx={{
                mt: 2,
                pt: 2,
                borderTop: "1px solid",
                borderColor: "warning.main",
              }}
            >
              <Alert
                severity={debugResult.collision.detected ? "error" : "success"}
                sx={{ mb: 2 }}
              >
                {debugResult.collision.detected
                  ? `Collision detected! ${debugResult.collision.pairs.length} component pairs overlap.`
                  : "No collision detected. Aircraft can coexist at these positions."}
              </Alert>

              {debugResult.collision.detected && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" fontWeight={600}>
                    Colliding Pairs:
                  </Typography>
                  {debugResult.collision.pairs.map((pair, idx) => (
                    <Typography key={idx} variant="body2" sx={{ ml: 2 }}>
                      {pair.a} ↔ {pair.b}: {pair.reason}
                    </Typography>
                  ))}
                </Box>
              )}

              {/* Debug info for each aircraft */}
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                {debugResult.placements.map((p, idx) => (
                  <Paper
                    key={idx}
                    sx={{ p: 1.5, bgcolor: "grey.100", minWidth: 300 }}
                  >
                    <Typography variant="subtitle2">{p.name}</Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      component="div"
                    >
                      Position: ({p.x}, {p.y}) @ {p.angle}°
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      component="div"
                    >
                      Centroid: ({p.debug.centroid[0].toFixed(1)},{" "}
                      {p.debug.centroid[1].toFixed(1)})
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      component="div"
                    >
                      Inverted: {p.debug.inverted ? "Yes" : "No"}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      component="div"
                    >
                      Main bbox: [{p.debug.bboxMain.minX.toFixed(1)},{" "}
                      {p.debug.bboxMain.minY.toFixed(1)}] to [
                      {p.debug.bboxMain.maxX.toFixed(1)},{" "}
                      {p.debug.bboxMain.maxY.toFixed(1)}]
                    </Typography>
                    {p.components && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" fontWeight={600}>
                          Components:
                        </Typography>
                        {Object.entries(p.components).map(([name, comp]) => (
                          <Typography
                            key={name}
                            variant="caption"
                            color="text.secondary"
                            component="div"
                            sx={{ ml: 1 }}
                          >
                            {name}: {comp.bottom.toFixed(1)}-
                            {comp.top.toFixed(1)}ft
                            {p.debug.bboxComponents[name] && (
                              <>
                                {" "}
                                | bbox [
                                {p.debug.bboxComponents[name].minX.toFixed(
                                  1
                                )},{" "}
                                {p.debug.bboxComponents[name].minY.toFixed(1)}]
                                to [
                                {p.debug.bboxComponents[name].maxX.toFixed(1)},{" "}
                                {p.debug.bboxComponents[name].maxY.toFixed(1)}]
                              </>
                            )}
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </Paper>
                ))}
              </Box>

              {/* Visual rendering of debug placements */}
              {selectedScenario && (
                <Box sx={{ mt: 2 }}>
                  <Typography
                    variant="caption"
                    fontWeight={600}
                    sx={{ mb: 1, display: "block" }}
                  >
                    Visual Debug (components enabled, height labels shown):
                  </Typography>
                  <HangarCanvas
                    hangar={selectedScenario.hangar}
                    placements={debugResult.placements.map((p) => ({
                      aircraft_id: p.aircraft_id,
                      x: p.x,
                      y: p.y,
                      angle: p.angle,
                      geometry: p.geometry,
                      components: p.components,
                      aircraft: {
                        model: p.name,
                        id: p.aircraft_id,
                      } as unknown as Aircraft,
                    }))}
                    wallMargin={selectedScenario.wallMargin ?? 2}
                    deadZones={[]}
                    showComponents={true}
                    onToggleComponents={() => {}}
                    showAircraftLabels={true}
                    onToggleAircraftLabels={() => {}}
                    showHeightLabels={true}
                    onToggleHeightLabels={() => {}}
                    showTowability={false}
                    onToggleTowability={() => {}}
                    showMagneticLines={false}
                    onToggleMagneticLines={() => {}}
                    showDeadZones={false}
                    onToggleDeadZones={() => {}}
                    showObstructions={false}
                    onToggleObstructions={() => {}}
                    showObstaclePlacements={false}
                    onToggleObstaclePlacements={() => {}}
                    bays={[]}
                    showBays={false}
                    onToggleBays={() => {}}
                    showTowAnimation={false}
                    onToggleTowAnimation={() => {}}
                    showSweptGeometry={false}
                    onToggleSweptGeometry={() => {}}
                    showTugGeometry={false}
                    onToggleShowTugGeometry={() => {}}
                    extractionOrder={[]}
                    debugRotation={debugRotation}
                    debugCentroids={debugResult.placements.map((p) => ({
                      x: p.debug.centroid[0],
                      y: p.debug.centroid[1],
                      label: p.name,
                    }))}
                    collisionPairs={debugResult.collision.pairs}
                  />
                </Box>
              )}
            </Box>
          )}
        </Paper>
      )}

      {/* Single Run Results */}
      {result && (
        <Box sx={{ display: "flex", gap: 2 }}>
          {/* Canvas */}
          <Box sx={{ flex: 2 }}>
            <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Mode: {result.scoringMode} | Engine: {result.engine || "typescript"}
              </Typography>
              {result.totalFootprintArea != null && (
                <Typography variant="body2" color="primary" fontWeight={600}>
                  {Math.round(
                    (result.totalFootprintArea /
                      (result.hangar.width * result.hangar.depth)) *
                      100
                  )}
                  % utilization
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary">
                {result.totalDurationMs.toFixed(0)}ms
              </Typography>
            </Box>
            <HangarCanvas
              hangar={result.hangar}
              placements={result.placements}
              wallMargin={result.wallMargin}
              deadZones={result.deadZones}
              showComponents={showComponents}
              onToggleComponents={setShowComponents}
              showAircraftLabels={showAircraftLabels}
              onToggleAircraftLabels={setShowAircraftLabels}
              showHeightLabels={showHeightLabels}
              onToggleHeightLabels={setShowHeightLabels}
              showTowability={showTowability}
              onToggleTowability={setShowTowability}
              showMagneticLines={showMagneticLines}
              onToggleMagneticLines={setShowMagneticLines}
              showDeadZones={showDeadZones}
              onToggleDeadZones={setShowDeadZones}
              obstructions={result.obstructions}
              showObstructions={showObstructions}
              onToggleObstructions={setShowObstructions}
              obstaclePlacements={result.obstaclePlacements}
              showObstaclePlacements={showObstaclePlacements}
              onToggleObstaclePlacements={setShowObstaclePlacements}
              bays={result.hangar.bays}
              showBays={showBays}
              onToggleBays={setShowBays}
              showGarageDoors={showGarageDoors}
              onToggleGarageDoors={setShowGarageDoors}
              showTowAnimation={showTowAnimation}
              onToggleTowAnimation={setShowTowAnimation}
              showSweptGeometry={showSweptGeometry}
              onToggleSweptGeometry={setShowSweptGeometry}
              showTugGeometry={showTugGeometry}
              onToggleShowTugGeometry={setShowTugGeometry}
              extractionOrder={extractionOrder}
              debugRotation={debugRotation}
              doors={result.scenario.doors}
            />
          </Box>

          {/* Decision Log */}
          <Box sx={{ flex: 1, maxHeight: 600, overflow: "auto" }}>
            <DecisionLog
              log={result.log}
              errors={result.errors}
              placements={result.placements}
              towPlan={result.towPlan}
              doors={result.scenario.doors}
            />
          </Box>
        </Box>
      )}

      {/* Exploration Results */}
      {exploreResult &&
        exploreResult.topVariants.length > 0 &&
        (() => {
          const selectedVariant =
            exploreResult.topVariants[selectedVariantIndex];
          const hangar = exploreResult.scenario.hangar;
          return (
            <Box>
              {/* Stats bar */}
              <Paper sx={{ p: 1.5, mb: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    gap: 3,
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Explored{" "}
                    {exploreResult.stats.phase1Count +
                      exploreResult.stats.phase2Count}{" "}
                    variants in {exploreResult.stats.totalDurationMs}ms
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    ({exploreResult.stats.variantsPerSecond}/sec)
                  </Typography>
                  <Typography variant="body2" color="primary" fontWeight={600}>
                    Best Score: {exploreResult.bestScore.toFixed(3)}
                  </Typography>
                </Box>
              </Paper>

              {/* Variant selector */}
              <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
                {exploreResult.topVariants.map((variant, idx) => (
                  <Tooltip
                    key={variant.variantId}
                    title={
                      <Box>
                        <div>
                          Density: {(variant.scores.density * 100).toFixed(1)}%
                        </div>
                        <div>
                          Towability:{" "}
                          {(variant.scores.towability * 100).toFixed(1)}%
                        </div>
                        <div>
                          Arrangement:{" "}
                          {(variant.scores.arrangement * 100).toFixed(1)}%
                        </div>
                        <div>
                          Placed:{" "}
                          {(variant.scores.placementRate * 100).toFixed(0)}%
                        </div>
                        <div>Phase: {variant.phase}</div>
                      </Box>
                    }
                  >
                    <Chip
                      label={`#${idx + 1}: ${variant.combinedScore.toFixed(3)}`}
                      color={
                        idx === selectedVariantIndex ? "primary" : "default"
                      }
                      variant={
                        idx === selectedVariantIndex ? "filled" : "outlined"
                      }
                      onClick={() => setSelectedVariantIndex(idx)}
                      sx={{ cursor: "pointer" }}
                    />
                  </Tooltip>
                ))}
              </Box>

              {/* Selected variant display */}
              <Box sx={{ display: "flex", gap: 2 }}>
                <Box sx={{ flex: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Variant: {selectedVariant.variantId}
                    </Typography>
                    {selectedVariant.layout.totalFootprintArea != null && (
                      <Typography
                        variant="body2"
                        color="primary"
                        fontWeight={600}
                      >
                        {Math.round(
                          (selectedVariant.layout.totalFootprintArea /
                            (hangar.width * hangar.depth)) *
                            100
                        )}
                        % utilization
                      </Typography>
                    )}
                    <Typography variant="body2" color="text.secondary">
                      {selectedVariant.layout.placements.length}/
                      {exploreResult.scenario.aircraftCount} placed
                    </Typography>
                  </Box>
                  <HangarCanvas
                    hangar={hangar}
                    placements={selectedVariant.layout.placements}
                    wallMargin={2}
                    deadZones={selectedVariant.layout.deadZones}
                    showComponents={showComponents}
                    onToggleComponents={setShowComponents}
                    showAircraftLabels={showAircraftLabels}
                    onToggleAircraftLabels={setShowAircraftLabels}
                    showHeightLabels={showHeightLabels}
                    onToggleHeightLabels={setShowHeightLabels}
                    showTowability={showTowability}
                    onToggleTowability={setShowTowability}
                    showMagneticLines={showMagneticLines}
                    onToggleMagneticLines={setShowMagneticLines}
                    showDeadZones={showDeadZones}
                    onToggleDeadZones={setShowDeadZones}
                    obstructions={selectedVariant.layout.obstructions}
                    showObstructions={showObstructions}
                    onToggleObstructions={setShowObstructions}
                    obstaclePlacements={selectedVariant.layout.obstaclePlacements}
                    showObstaclePlacements={showObstaclePlacements}
                    onToggleObstaclePlacements={setShowObstaclePlacements}
                    bays={[]}
                    showBays={showBays}
                    onToggleBays={setShowBays}
                    showGarageDoors={showGarageDoors}
                    onToggleGarageDoors={setShowGarageDoors}
                    showTowAnimation={showTowAnimation}
                    onToggleTowAnimation={setShowTowAnimation}
                    showSweptGeometry={showSweptGeometry}
                    onToggleSweptGeometry={setShowSweptGeometry}
                    showTugGeometry={showTugGeometry}
                    onToggleShowTugGeometry={setShowTugGeometry}
                    extractionOrder={exploreExtractionOrder}
                    debugRotation={debugRotation}
                    doors={selectedScenario?.doors}
                  />
                </Box>

                {/* Decision Log */}
                <Box sx={{ flex: 1, maxHeight: 600, overflow: "auto" }}>
                  <DecisionLog
                    log={selectedVariant.layout.log}
                    errors={selectedVariant.layout.errors}
                    placements={selectedVariant.layout.placements}
                    towPlan={selectedVariant.towPlan}
                    doors={selectedScenario?.doors}
                  />
                </Box>
              </Box>
            </Box>
          );
        })()}

      {/* Empty state */}
      {!result &&
        !exploreResult &&
        !loading &&
        !exploring &&
        scenarios.length > 0 && (
          <Paper
            sx={{
              p: 4,
              textAlign: "center",
              color: "text.secondary",
            }}
          >
            <Typography>
              Select a scenario and click Run to see the layout result
            </Typography>
          </Paper>
        )}
    </Box>
  );
};
